import { Directive, Input, OnChanges, SimpleChanges, HostBinding, ElementRef, inject, OnInit } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import {
  PresignedPhoto, ListingPhoto, PresignService, PresignedThumbnail,
} from '@lc/core';
import { getNativeElement } from '@lc/products/lib/shared/components/util/util';

@Directive()
abstract class PresignedDirective implements OnChanges, OnInit {
  private readonly service = inject(PresignService);
  private readonly element = inject(ElementRef<HTMLImageElement>);

  private _presignedPhoto: PresignedPhoto;
  public get presignedPhoto(): PresignedPhoto { return this._presignedPhoto; }
  abstract photo: ListingPhoto;

  @Input() size?: number;

  @Input() fallbackSize?: number;

  retryCount = 0;

  ngOnInit() {
    getNativeElement(this.element).onerror = () => {
      if (this.retryCount < 10) {
        // Retry until it works...
        this.retryCount += 1;
        this.setImageSource('');
        setTimeout(() => this.setPresignedPhoto(this.presignedPhoto), 100);
      }
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.photo || changes.size) {
      this.retryCount = 0;
      if (this._presignedPhoto) {
        this.setPresignedPhoto(this._presignedPhoto);
      } else {
        this.service.presignPhoto(this.photo).subscribe((presigned) => this.setPresignedPhoto(presigned));
      }
    }
  }

  abstract setImageSource(source: string);

  private setPresignedPhoto(presignedPhoto: PresignedPhoto) {
    // Cache the photo for future use
    this._presignedPhoto = presignedPhoto;

    const requestedThumbnail = this.findRequestedThumbnail(presignedPhoto);
    const source = requestedThumbnail
      ? requestedThumbnail.presignedUrl || requestedThumbnail.originalUrl
      : presignedPhoto?.presignedUrl || presignedPhoto?.originalUrl;

    // We need to detect errors loading the image on the <img/> element.
    this.setImageSource(source);
  }

  private findRequestedThumbnail(presigned: PresignedPhoto): PresignedThumbnail | null {
    if (!this.size) {
      return null; // If size is not specified, return null
    }

    // If a size is specified, lets find the requested thumbnail. If not found, find the fallback
    const requestedThumbnail = presigned?.locateThumbnail(this.size, this.fallbackSize) ?? null;
    return requestedThumbnail;
  }
}
@Directive({ selector: '[lcPresignedPhoto]' })
export class PresignedPhotoDirective extends PresignedDirective {
  @HostBinding('attr.src')
    imageSource: string = '';

  @Input('lcPresignedPhoto') // Keep the name of this input the same as the directive selector name
    photo: ListingPhoto;

  setImageSource(source: string) {
    this.imageSource = source;
  }
}

@Directive({ selector: '[lcPresignedPhotoBackground]' })
export class PresignedPhotoBackgroundDirective extends PresignedDirective {
  private sanitizer = inject(DomSanitizer);

  @Input('lcPresignedPhotoBackground') // Keep the name of this input the same as the directive selector name
    photo: ListingPhoto;

  @HostBinding('style.background-image')
  public backgroundImage: SafeStyle;

  setImageSource(source: string) {
    this.backgroundImage = this.sanitizer.bypassSecurityTrustStyle(`url("${source}")`);
  }
}
